/**
 * Where a visitor who arrived from the study app goes back to.
 *
 * Reads the same `from=study-guide` + `studyHost` pair that
 * `packages/objectives/src/returnTo.ts` already reads, rather than inventing a second
 * marker for the same journey.
 *
 * Exact approved hosts only. A query parameter that reached an arbitrary host would
 * turn this page into an open redirect, so an unrecognised `studyHost` falls back to
 * production rather than being trusted.
 *
 * Keep this list in sync with `DEV_STUDY_HOSTS` in `packages/objectives/src/returnTo.ts`
 * and with `sgapp-client src/pages/preview/objectivesUrl.ts`. They are three packages
 * with three deploy artifacts, so the list is copied rather than imported — and it
 * drifted the first time this copy was written. A host missing here does not fail
 * loudly; it silently sends a development visitor to production.
 */
const STUDY_HOSTS = new Set([
  'study.240tutoring.com',
  'study.240.org',
  'study.dev.240tutoring.com',
  'sgapp.dev.240tutoring.com',
  'd34001vcvoskd2.cloudfront.net',
  'd1pkm6cwi6dnf6.cloudfront.net',
  'd3immiatbdnalb.cloudfront.net',
  'samtest.240tutoring.com',
  'clt-dev.240tutoring.com',
  'study-dev.bestinclass.com',
]);

/** The study app's home, used when a production arrival names no host we recognise. */
export const STUDY_HOME = 'https://study.240tutoring.com/';

/** The same, for a visitor who is on the development Labs site. */
export const STUDY_HOME_DEV = 'https://study.dev.240tutoring.com/';

/** The development Labs site, which must never hand a teacher to production. `preview.dev.240.org` is its name from before the Labs rename and still routes. */
const DEV_HOSTS = new Set(['labs.dev.240.org', 'preview.dev.240.org']);

/** Whether the study app sent this visitor here, rather than their arriving on their own. */
export function arrivedFromStudy(search: string): boolean {
  return new URLSearchParams(search).get('from') === 'study-guide';
}

/**
 * The routes `landing_page_viewed` can name. `study-guide` is the header chip in the
 * study app; `preview-updates` is a link in the monthly email, which is what makes an
 * email-driven tool open attributable at all. Anything else is `direct`, including the
 * `from=marketing` the tools put on their way back here.
 */
export type ArrivedFrom = 'study-guide' | 'preview-updates' | 'direct';

export function arrivedFrom(search: string): ArrivedFrom {
  const from = new URLSearchParams(search).get('from');
  if (from === 'study-guide' || from === 'preview-updates') return from;
  return 'direct';
}

/**
 * The study app's own `/preview` redirects to this page. Every target here is
 * therefore the app's home and never `/preview`: a link back to `/preview` would
 * bounce the teacher straight back here and loop.
 *
 * `hostname` is this page's own host, and it decides the fallback. The study app only
 * appends `studyHost` on its development hosts, so a development arrival routinely
 * names none — and a fallback that ignored our own environment would send that teacher
 * to production, a different app with different data.
 */
export function studyReturnUrl(search: string, hostname: string): string | null {
  const params = new URLSearchParams(search);
  if (!arrivedFromStudy(search)) return null;
  const host = params.get('studyHost') ?? '';
  if (STUDY_HOSTS.has(host)) return `https://${host}/`;
  return DEV_HOSTS.has(hostname) ? STUDY_HOME_DEV : STUDY_HOME;
}

/**
 * Where the header's Study button goes. Every visitor gets one — the page is reached
 * from the study app's own header, and a teacher who lands here from a shared link
 * still has a study account to go back to. A visitor the study app sent here returns
 * to the host they came from; anyone else goes to the study app's home for this
 * environment.
 */
export function studyHomeUrl(search: string, hostname: string): string {
  return studyReturnUrl(search, hostname) ?? (DEV_HOSTS.has(hostname) ? STUDY_HOME_DEV : STUDY_HOME);
}
